import { useAuth } from 'react-oidc-context';
import { Client } from '../client-data-types';
import { API_BASE_URL } from 'src/features/shared/data/app-settings';
import { useCallback } from 'react';
import { ResponseObject } from 'src/utils/types';

const useClientApi = () => {
    const { user } = useAuth();

    const updateClient = useCallback(
        async (client: Client): Promise<ResponseObject<Client | null>> => {
            const resp = await fetch(new URL(`${API_BASE_URL}/client/${client.id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user?.access_token}`,
                },
                body: JSON.stringify(client),
            });
            const data = await resp.json();
            if (!resp.ok) {
                console.error('Error in updateClient:', data);
                return {
                    errorStatusCode: +data.statusCode,
                    response: null,
                } as ResponseObject<Client | null>;
            }

            return {
                errorStatusCode: null,
                response: data as Client,
            } as ResponseObject<Client | null>;
        },
        [user],
    );

    const addClient = useCallback(
        async (client: Client): Promise<ResponseObject<string | null>> => {
            const resp = await fetch(new URL(`${API_BASE_URL}/client`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user?.access_token}`,
                },
                body: JSON.stringify(client),
            });
            const data = await resp.json();

            if (!resp.ok) {
                console.error('Error in addClient:', data);
                return {
                    errorStatusCode: +data.statusCode,
                    response: null,
                } as ResponseObject<string | null>;
            }

            return {
                errorStatusCode: null,
                response: data as string,
            } as ResponseObject<string | null>;
        },
        [user],
    );

    const deleteClient = useCallback(
        async (clientId: string): Promise<ResponseObject<boolean>> => {
            const resp = await fetch(new URL(`${API_BASE_URL}/client/${clientId}`), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user?.access_token}`,
                },
            });
            const data = await resp.json();

            if (!resp.ok) {
                console.error('Error in deleteClient:', data);
                return {
                    errorStatusCode: +data.statusCode,
                    response: false,
                } as ResponseObject<boolean>;
            }

            return {
                errorStatusCode: null,
                response: data as boolean,
            } as ResponseObject<boolean>;
        },
        [user],
    );

    const getClient = useCallback(
        async (clientId: string): Promise<ResponseObject<Client | null>> => {
            const resp = await fetch(new URL(`${API_BASE_URL}/client/${clientId}`), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user?.access_token}`,
                },
            });
            const data = await resp.json();
            if (!resp.ok) {
                console.error('Error in getClient:', data);
                return {
                    errorStatusCode: +data.statusCode,
                    response: null,
                } as ResponseObject<Client | null>;
            }

            return {
                errorStatusCode: null,
                response: data as Client,
            } as ResponseObject<Client | null>;
        },
        [user],
    );

    return {
        updateClient,
        deleteClient,
        getClient,
        addClient,
    };
};

export default useClientApi;
